import { useMemo } from 'react'
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

interface TreeNode {
  id: string
  name: string
  nodeType: 'folder' | 'leaf'
  children?: TreeNode[]
}

interface CategoryMindMapProps {
  data: TreeNode[]
}

interface LayoutNode {
  id: string
  x: number
  y: number
  isLeaf: boolean
  label: string
}

const NODE_WIDTH = 150
const NODE_HEIGHT = 50
const HORIZONTAL_GAP = 200
const VERTICAL_GAP = 20

function calculateLayout(nodes: TreeNode[], startX = 0, startY = 0): LayoutNode[] {
  const result: LayoutNode[] = []

  function layout(node: TreeNode, x: number, y: number): number {
    const children = node.children || []
    if (children.length === 0) {
      result.push({ id: node.id, x, y, isLeaf: node.nodeType === 'leaf', label: node.name })
      return NODE_HEIGHT
    }

    let totalChildHeight = 0
    const childYPositions: number[] = []

    for (const child of children) {
      const childHeight = layout(child, x + HORIZONTAL_GAP, y + totalChildHeight)
      childYPositions.push(y + totalChildHeight)
      totalChildHeight += childHeight + VERTICAL_GAP
    }

    totalChildHeight -= VERTICAL_GAP
    const parentY = childYPositions[0] + (childYPositions[childYPositions.length - 1] - childYPositions[0]) / 2
    result.push({ id: node.id, x, y: parentY, isLeaf: node.nodeType === 'leaf', label: node.name })
    return totalChildHeight
  }

  let currentY = startY
  for (const node of nodes) {
    const height = layout(node, startX, currentY)
    currentY += height + VERTICAL_GAP
  }
  return result
}

export function CategoryMindMap({ data }: CategoryMindMapProps) {
  const { flowNodes, flowEdges } = useMemo(() => {
    const layoutNodesResult = calculateLayout(data)

    const flowNodes: Node[] = layoutNodesResult.map((node) => ({
      id: node.id,
      position: { x: node.x, y: node.y },
      data: { label: node.label, isLeaf: node.isLeaf },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      showHandle: false,
      style: {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        background: node.isLeaf ? '#f0fdf4' : '#eff6ff',
        border: node.isLeaf ? '2px solid #22c55e' : '2px solid #3b82f6',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 500,
        color: '#1f2937',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }))

    const flowEdges: Edge[] = []

    function buildEdges(nodes: TreeNode[], parentId?: string) {
      for (const node of nodes) {
        if (parentId) {
          flowEdges.push({
            id: `${parentId}-${node.id}`,
            source: parentId,
            target: node.id,
            type: 'straight',
            style: { stroke: '#9ca3af', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#9ca3af' },
          })
        }
        if (node.children) buildEdges(node.children, node.id)
      }
    }

    buildEdges(data)
    return { flowNodes, flowEdges }
  }, [data])

  const [nodes] = useNodesState(flowNodes)
  const [edges] = useEdgesState(flowEdges)

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        style={{ background: '#f9fafb' }}
      >
        <Background color="#e5e7eb" gap={20} />
        <Controls position="bottom-right" />
      </ReactFlow>
    </div>
  )
}
